import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetAllVideosQuery } from "../../services/modules/video";
import { useGetAllNetworksQuery } from "../../services/modules/network";
import {
  useGetAllTop5Query,
  useCreateTop5Mutation,
} from "../../services/modules/top5";

import {
  setSelectedNetwork,
  setSelectedVideos,
  addVideoToTop5,
  removeVideoFromTop5,
} from "../../store/top5";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* Badge rete come negli altri componenti */
const NetworkBadge = ({ name }) => (
  <span className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-gray-100 border mr-1 mt-1">
    {name}
  </span>
);

const Top5List = () => {
  const maxVideos = 20; // Numero massimo di video nella Top
  const dispatch = useDispatch();

  const selectedNetwork = useSelector((state) => state.top5.selectedNetwork);
  const selectedVideos = useSelector((state) => state.top5.selectedVideos);

  const { data: networksData } = useGetAllNetworksQuery();
  const { data: videosData, isLoading: loadingVideos } = useGetAllVideosQuery({
    page: 1,
    pageSize: 2000,
  });
  const [createTop5, { isLoading: saving }] = useCreateTop5Mutation();

  const {
    data: top5Data,
    refetch: refetchTop5,
  } = useGetAllTop5Query(
    { networkId: selectedNetwork },
    { skip: !selectedNetwork }
  );

  const [searchTerm, setSearchTerm] = useState("");

  const networks = networksData?.data?.result || [];
  const videos = videosData?.data?.result || [];

  useEffect(() => {
    if (selectedNetwork) refetchTop5();
  }, [selectedNetwork, refetchTop5]);

  useEffect(() => {
    if (top5Data?.top5 && top5Data?.videos) {
      const sortedTop5 = [...top5Data.top5].sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
      const videoMap = new Map(top5Data.videos.map((v) => [v.id, v]));
      const mergedTop5 = sortedTop5.map((top5Video) => ({
        ...top5Video,
        thumbnail: videoMap.get(top5Video.id)?.thumbnail || "",
        title: videoMap.get(top5Video.id)?.title || "(Untitled)",
        networks: videoMap.get(top5Video.id)?.networks || [], // 👈 porto anche le reti
      }));
      dispatch(setSelectedVideos(mergedTop5));
    } else {
      dispatch(setSelectedVideos([]));
    }
  }, [top5Data, selectedNetwork, dispatch]);

  // --- FILTRO SOLO LATO CLIENT: video.networks: [{id, name}] ---
  const videosByNetwork = selectedNetwork
    ? videos.filter(
        (v) =>
          Array.isArray(v.networks) &&
          v.networks.some((n) => String(n.id) === String(selectedNetwork))
      )
    : [];

  const filteredVideos = videosByNetwork.filter((v) =>
    `${v.title}`.toLowerCase().includes(searchTerm.toLowerCase())
  );
  // --- FINE FILTRO ---

  const handleSave = async () => {
    if (!selectedNetwork || selectedVideos.length === 0) {
      toast.error("Seleziona una rete e almeno un video!");
      return;
    }
    try {
      const payload = new URLSearchParams({
        networkId: selectedNetwork,
        top5: JSON.stringify(
          selectedVideos.map((v, index) => ({
            id: v.id,
            displayOrder: index + 1,
          }))
        ),
      }).toString();

      await createTop5(payload).unwrap();
      toast.success("Top salvata con successo!");
      refetchTop5();
    } catch (error) {
      console.error("Save failed:", error);
      toast.error("Salvataggio non riuscito. Riprova.");
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = selectedVideos.findIndex(
        (v) => v.id.toString() === active.id
      );
      const newIndex = selectedVideos.findIndex(
        (v) => v.id.toString() === over.id
      );
      const reordered = arrayMove(selectedVideos, oldIndex, newIndex);
      dispatch(setSelectedVideos(reordered));
    }
  };

  const onAdd = (video) => {
    const exists = selectedVideos.some((sv) => sv.id === video.id);
    if (exists) return;
    dispatch(addVideoToTop5(video));
    toast.success("Video aggiunto");
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-6">Top</h2>

      <div className="mb-6">
        <label className="font-medium block mb-2">Seleziona Rete</label>
        <select
          value={selectedNetwork ?? ""}
          onChange={(e) => {
            const value = e.target.value ? Number(e.target.value) : "";
            dispatch(setSelectedNetwork(value));
            setSearchTerm(""); // reset ricerca al cambio rete
          }}
          className="border p-2 rounded w-1/3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
        >
          <option value="">-- Seleziona Rete --</option>
          {networks.map((network) => (
            <option key={network.id} value={network.id}>
              {network.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4 w-[28%]">
        <input
          type="text"
          placeholder="Cerca video..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Video Disponibili */}
        <div className="border p-4 rounded h-[500px] overflow-y-auto bg-white">
          <h4 className="font-bold mb-2">Video Disponibili</h4>

          {!selectedNetwork && (
            <p className="text-sm text-gray-500">
              Seleziona una rete per vedere i video disponibili.
            </p>
          )}

          {selectedNetwork && loadingVideos && <p>Caricamento video...</p>}

          {selectedNetwork && !loadingVideos && filteredVideos.length === 0 && (
            <p className="text-sm text-gray-500">Nessun video trovato.</p>
          )}

          {selectedNetwork &&
            !loadingVideos &&
            filteredVideos.map((video) => {
              const already = selectedVideos.some((sv) => sv.id === video.id);
              return (
                <div
                  key={video.id}
                  className="flex justify-between items-center p-2 border-b"
                >
                  <div className="flex gap-2 items-center min-w-0">
                    <img
                      src={video.thumbnail || "https://via.placeholder.com/50x30"}
                      alt="thumbnail"
                      className="w-16 h-10 object-cover rounded flex-shrink-0"
                    />
                    <div className="min-w-0">
                      {/* Titolo: tronco a 250px, niente a capo */}
                      <span className="block max-w-[250px] truncate">
                        {video.title}
                      </span>
                      {/* Badge reti */}
                      <div className="mt-0.5">
                        {(video.networks || []).map((n) => (
                          <NetworkBadge key={n.id} name={n.name} />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Bottone: disabilitato se già aggiunto */}
                  <button
                    onClick={() => !already && onAdd(video)}
                    disabled={already}
                    className={`px-2 py-1 rounded text-sm ml-2 ${
                      already
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-red-600 hover:underline"
                    }`}
                    title={already ? "Già aggiunto" : "Aggiungi alla Top"}
                  >
                    {already ? "Già aggiunto" : "+ Aggiungi"}
                  </button>
                </div>
              );
            })}
        </div>

        {/* Video Selezionati */}
        <div className="border p-4 rounded h-[500px] overflow-y-auto bg-white">
          <h4 className="font-bold mb-2">
            Video Selezionati ({selectedVideos.length}/{maxVideos})
          </h4>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedVideos.map((v) => v.id.toString())}
              strategy={verticalListSortingStrategy}
            >
              {selectedVideos.map((video) => (
                <SortableVideoItem
                  key={video.id}
                  video={video}
                  removeVideo={(id) => dispatch(removeVideoFromTop5(id))}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>

      <div className="mt-6 text-center">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-gray-800 text-white px-6 py-2 rounded hover:bg-gray-700 disabled:opacity-50"
        >
          {saving ? "Sto salvando..." : "Salva Top"}
        </button>
      </div>

      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
};

export default Top5List;

const SortableVideoItem = ({ video, removeVideo }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: video.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex justify-between items-center p-2 border-b"
    >
      <div
        className="flex gap-2 items-center cursor-grab min-w-0"
        {...attributes}
        {...listeners}
      >
        <img
          src={video.thumbnail || "https://via.placeholder.com/50x30"}
          alt="thumbnail"
          className="w-16 h-10 object-cover rounded flex-shrink-0"
        />
        <div className="min-w-0">
          {/* Titolo: tronco a 250px, niente a capo */}
          <span className="max-w-[250px] truncate block">{video.title}</span>
          {/* Se vuoi mostrare i badge reti anche qui, decommenta: */}
          {/* <div className="mt-0.5">
            {(video.networks || []).map((n) => (
              <NetworkBadge key={n.id} name={n.name} />
            ))}
          </div> */}
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          removeVideo(video.id);
        }}
        className="text-red-600 hover:underline ml-4"
      >
        Rimuovi
      </button>
    </div>
  );
};
